import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

const FORM_URL = 'https://readdy.ai/api/form/d9v4jchkngcel2k3jcrg';

const PDF_ES = 'https://storage.helloreaddy.io/project_files/33950b86-7657-493b-8177-e5932d2de9a1/5816cdf5-9b0b-4cea-ac2f-2d881c219114_Bonus01_Guia_Emergencia_Nocturna.pdf';
const PDF_EN = 'https://storage.helloreaddy.io/project_files/33950b86-7657-493b-8177-e5932d2de9a1/ae02f27f-2fa0-4828-8efa-d8850f162f2a_Bonus01_Nighttime_Emergency_Guide_EN.pdf';

async function downloadGuide(lang: 'es' | 'en') {
  const url = lang === 'en' ? PDF_EN : PDF_ES;
  const filename = lang === 'en' ? 'Nighttime_Emergency_Guide.pdf' : 'Guia_Emergencia_Nocturna.pdf';
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch {
    window.open(url, '_blank');
  }
}

interface EmergencyGuideBannerProps {
  variant?: 'default' | 'gratis';
}

export default function EmergencyGuideBanner({ variant = 'default' }: EmergencyGuideBannerProps) {
  const { t, i18n } = useTranslation();
  const isGratisVariant = variant === 'gratis';
  const [showForm, setShowForm] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const lang: 'es' | 'en' = i18n.language === 'en' ? 'en' : 'es';

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('company_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      downloadGuide(lang);
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'company_alt' && String(value).trim() === '') return;
      params.append(key, String(value));
    });

    setStatus('loading');
    setFormError('');
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '';
      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        downloadGuide(lang);
      } else {
        setStatus('error');
        setFormError(serverMsg || t('home.guide.form.error'));
      }
    } catch {
      setStatus('error');
      setFormError(t('home.guide.form.error'));
    }
  };

  return (
    <div className="relative overflow-hidden rounded-2xl bg-[#2D5016] px-6 md:px-10 py-10 md:py-12">
      <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-6">
        <div className="text-center lg:text-left max-w-2xl">
          <h3 className="font-serif text-2xl md:text-3xl font-bold text-[#F7F2EA] mb-3 leading-tight">
            🎁 {t(isGratisVariant ? 'home.guide.title_gratis' : 'home.guide.title', 'Regalo para esta noche')}
          </h3>
          <p className="font-sans text-sm md:text-base text-[#EDE4D6] leading-relaxed">
            {t(isGratisVariant ? 'home.guide.text_gratis' : 'home.guide.text', 'Descargá gratis la Guía de Emergencia Nocturna — qué hacer en los próximos 90 segundos cuando todo se desarmó y no podés pensar.')}
          </p>
        </div>
        <div className="w-full lg:w-auto flex-shrink-0">
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="w-full lg:w-auto inline-flex items-center justify-center bg-terracotta text-white px-8 py-4 rounded-full font-sans text-sm font-semibold hover:bg-brown transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              {t('home.guide.cta', 'Descargar gratis →')}
            </button>
          )}
        </div>
      </div>

      {showForm && (
        <div className="relative z-10 mt-8">
          {status === 'success' ? (
            <div className="bg-[#FDFAF6] rounded-xl px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="font-sans text-sm text-[#2D5016] font-semibold">
                {t('home.guide.form.success', '¡Listo! Tu guía se está descargando 🎉')}
              </p>
              <button
                onClick={() => downloadGuide(lang)}
                className="inline-flex items-center justify-center bg-[#2D5016] text-[#FDFAF6] px-6 py-3 rounded-full font-sans text-sm font-semibold hover:bg-[#1F3A0E] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-download-2-line mr-2" />
                {t('home.guide.form.downloadAgain', 'Descargar de nuevo')}
              </button>
            </div>
          ) : (
            <form
              id="guia-emergencia-form"
              onSubmit={handleSubmit}
              data-readdy-form="guia-emergencia"
              className="bg-[#FDFAF6] rounded-xl p-6"
            >
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  name="email"
                  required
                  placeholder={t('home.guide.form.emailPlaceholder', 'tu@email.com')}
                  className="flex-1 px-5 py-3.5 rounded-full border border-[#EDE4D6] bg-white font-sans text-sm text-[#3D2B1F] outline-none focus:border-terracotta transition-colors duration-200"
                />
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-hp"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="inline-flex items-center justify-center bg-terracotta text-white px-7 py-3.5 rounded-full font-sans text-sm font-semibold hover:bg-brown transition-colors duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  {status === 'loading' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2" />
                      {t('home.guide.form.loading', 'Enviando...')}
                    </>
                  ) : (
                    t('home.guide.form.submit', 'Enviar y descargar')
                  )}
                </button>
              </div>
              {status === 'error' && formError && (
                <p className="mt-3 font-sans text-xs text-terracotta">
                  {formError}
                </p>
              )}
              <p className="mt-3 font-sans text-xs text-[#B0A090]">
                {t('home.guide.form.privacy', 'Sin spam. Podés darte de baja en cualquier momento.')}
              </p>
            </form>
          )}
        </div>
      )}
    </div>
  );
}